import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject, catchError, filter, map, repeat, tap, throwError } from 'rxjs';
import { ViewType } from 'src/app/core/services/view-api.service';
import { ParametersAsIntersection } from 'src/app/shared/models/type-helper';
import { setPrettyNameForPropDef } from '../../service-utils';
import { ExportToExcelService } from '../export-to-excel.service';
import {
  PropertyDefinitionsApiService,
  PropertyDefsDto,
  ReasonForIssueOutputDto,
} from '../property-definitions-api.service';

export type PropertyDefinitionsForViewParams = ParametersAsIntersection<
  typeof PropertyDefinitionsApiService.prototype.getPropertyDefinitionsForView
>;

@Injectable({ providedIn: 'root' })
export class PropertyDefinitionService {
  readonly #propertyDefinitionsApiService = inject(PropertyDefinitionsApiService);
  readonly #exportToExcelService = inject(ExportToExcelService);

  readonly #refetchPropertyDef$ = new Subject<boolean>();
  readonly #refetchPropertyViewDef$ = new Subject<boolean>();

  readonly #isPropertyDefLoading$ = new BehaviorSubject<boolean>(false);
  isPropertyDefLoading$ = this.#isPropertyDefLoading$.asObservable();

  readonly #isPropertyViewDefLoading$ = new BehaviorSubject<boolean>(false);
  isPropertyViewDefLoading$ = this.#isPropertyViewDefLoading$.asObservable();

  readonly #propertyDefinitions$ = new BehaviorSubject<UNDEFINED<unknown[]>>(undefined);
  propertyDefinitions$ = this.#propertyDefinitions$.asObservable().pipe(filter(Boolean));

  readonly #propertyViewDefinitions$ = new BehaviorSubject<UNDEFINED<PropertyDefsDto>>(undefined);
  propertyViewDefinitions$ = this.#propertyViewDefinitions$.asObservable().pipe(filter(Boolean));

  rowsToAdd$: Subject<number> = new Subject();

  get propertyDefLoadingInit$() {
    this.#isPropertyDefLoading$.next(true);
    return this.isPropertyDefLoading$;
  }

  get propertyViewDefLoadingInit$() {
    this.#isPropertyViewDefLoading$.next(true);
    return this.isPropertyViewDefLoading$;
  }

  refetchPropertyDef() {
    this.#refetchPropertyDef$.next(true);
    this.#isPropertyDefLoading$.next(true);
  }

  refetchPropertyViewDef() {
    this.#refetchPropertyViewDef$.next(true);
    this.#isPropertyViewDefLoading$.next(true);
  }

  propertyDefinitions(projectId: number) {
    this.#isPropertyDefLoading$.next(true);

    return this.#propertyDefinitionsApiService.getPropertyDefinitions({ projectId }).pipe(
      map(propDefs => {
        propDefs.forEach(propDef => setPrettyNameForPropDef(propDef));
        return propDefs;
      }),
      repeat({ delay: () => this.#refetchPropertyDef$ }),
      tap(result => this.#propertyDefinitions$.next(result)),
      tap(() => this.#isPropertyDefLoading$.next(false)),
      catchError(error => {
        this.#isPropertyDefLoading$.next(false);
        return throwError(() => error);
      }),
    );
  }

  propertyViewDefinitions(params: PropertyDefinitionsForViewParams) {
    this.#isPropertyViewDefLoading$.next(true);

    const { projectId, viewType, profileId } = params;
    let { classificationId, trimPackageId, indexId } = params;

    trimPackageId = getIsValidPackageId(params) ? params.trimPackageId : undefined;
    classificationId = params.classificationId?.toString();
    indexId = params.indexId?.toString();

    return this.#propertyDefinitionsApiService
      .getPropertyDefinitionsForView(
        { projectId, viewType },
        { trimPackageId, classificationId, indexId, profileId },
      )
      .pipe(
        repeat({ delay: () => this.#refetchPropertyViewDef$ }),
        tap(result => this.#propertyViewDefinitions$.next(result)),
        tap(() => this.#isPropertyViewDefLoading$.next(false)),
        catchError(error => {
          this.#isPropertyViewDefLoading$.next(false);
          return throwError(() => error);
        }),
        map(val => val as PropertyDefsDto), // TODO: remove type override
      );
  }

  getReasonForIssues(
    profileKind: 'interface' | 'tag' | 'model3d' | 'document',
    profileId: number,
  ): Observable<ReasonForIssueOutputDto[]> {
    const path = { profileKind, profileId };

    return this.#propertyDefinitionsApiService.getReasonForIssues(path).pipe(
      map(response => response as ReasonForIssueOutputDto[]),
      catchError(error => throwError(() => error)),
    );
  }

  exportToExcel() {
    return this.#exportToExcelService.export('/api/projects/propertydefinitions/excelexport');
  }
}

function getIsValidPackageId(params: PropertyDefinitionsForViewParams) {
  return (
    !!params.trimPackageId &&
    (['SUPPLIER_WORKSHEET', 'TRANSMITTAL_REVIEW_WORKSHEET'] as ViewType[]).includes(
      params.viewType as ViewType,
    )
  );
}
